import { useEffect, useState } from "react";
import { Box, Button, Container, Grid } from "@material-ui/core";
import AddIcon from "@mui/icons-material/Add";
import HeaderClientes from "components/_refactor/headers/clients";
import LoadingData from "components/Loading/LoadingData";
import NotFound from "components/Texts/NotFound";
import FilterCard from "components/_refactor/filter-card";
import InputText from "components/_refactor/text/input";
import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import FormNovaSolicitacao from "./components/FormNovaSolicitacao";
import TableSolicitacoes from "./components/TableSolicitacoes";
import EditSolicitacaoDialog from "./components/EditSolicitacaoDialog";
import HistoricoModal from "./components/HistoricoModal";

const PAGE_SIZE = 25;

function unwrapResult(response) {
  if (response?.Success === false) {
    throw new Error(
      response?.ErrorMessage ||
        response?.ValidationMessage ||
        "Erro na requisição.",
    );
  }
  return response?.Result ?? response?.result ?? response;
}

export default function SolicitacaoDevolucaoInterna() {
  const snackbar = useSnackbar();

  const [newRequestPage, setNewRequestPage] = useState(false);
  const [lista, setLista] = useState([]);
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [numeroNotaFiltro, setNumeroNotaFiltro] = useState("");

  const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);

  const [historicoSolicitacao, setHistoricoSolicitacao] = useState(null);
  const [openHistoricoModal, setOpenHistoricoModal] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const handleSearch = async (pageToLoad = page) => {
    setSearching(true);
    try {
      const pageIndex = pageToLoad > 0 ? pageToLoad - 1 : 0;
      let query = `?page=${pageIndex}&pageSize=${PAGE_SIZE}`;
      if (numeroNotaFiltro.trim()) {
        query += `&numeroNota=${encodeURIComponent(numeroNotaFiltro.trim())}`;
      }

      const res = await Api.get("list", "internalReturn", query, true);
      const data = unwrapResult(res);
      const items = data?.Lista || [];
      const total = data?.TotalRegistros || 0;

      setLista(items);
      setTotalPages(Math.max(1, Math.ceil(total / PAGE_SIZE)));
    } catch (error) {
      snackbar.showMessage(
        error?.message || "Não foi possível carregar as solicitações.",
        { severity: "error" },
      );
      setLista([]);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (!newRequestPage) {
      handleSearch(page);
    }
  }, [page, newRequestPage]);

  const handleClearFilters = () => {
    setNumeroNotaFiltro("");
    setPage(1);
    handleSearch(1);
  };

  const handleResendEmail = async (row) => {
    setResendingId(row.IdSolicitacaoDevolucaoInterna);
    try {
      const res = await Api.postParams(
        "resend-crc-email",
        "internalReturn",
        `?id=${row.IdSolicitacaoDevolucaoInterna}`,
        true,
      );
      unwrapResult(res);
      snackbar.showMessage("E-mail reenviado ao CRC com sucesso!", {
        severity: "success",
      });
      handleSearch(page);
    } catch (error) {
      snackbar.showMessage(
        error?.message || "Não foi possível reenviar o e-mail ao CRC.",
        { severity: "error" },
      );
    } finally {
      setResendingId(null);
    }
  };

  return (
    <>
      <HeaderClientes
        section="Solicitação de Devolução Interna"
        subsection="Gerenciamento"
      />
      <Container style={{ marginTop: "-4rem" }} maxWidth={false}>
        {newRequestPage ? (
          <FormNovaSolicitacao
            onBack={() => setNewRequestPage(false)}
            onCreated={() => {
              setNewRequestPage(false);
              setPage(1);
              handleSearch(1);
            }}
          />
        ) : (
          <>
            <Box
              display="flex"
              justifyContent="flex-end"
              mb={2}
              style={{ gap: 8 }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                onClick={() => setNewRequestPage(true)}
              >
                Nova Solicitação
              </Button>
            </Box>

            <FilterCard
              handleSearch={() => {
                setPage(1);
                handleSearch(1);
              }}
              disabled={searching}
              searching={searching}
              handleClearFilters={handleClearFilters}
            >
              <Grid item xs={12} md={3}>
                <InputText
                  label="Nota Fiscal"
                  value={numeroNotaFiltro}
                  onChange={(e) => {
                    setNumeroNotaFiltro(
                      e.target.value.replace(/\D/g, "").slice(0, 10),
                    );
                  }}
                />
              </Grid>
            </FilterCard>

            {searching ? (
              <LoadingData
                searching
                label="Buscando solicitações..."
                hiddenIcon
              />
            ) : lista.length > 0 ? (
              <TableSolicitacoes
                lista={lista}
                setPage={setPage}
                totalPages={totalPages}
                onEdit={(row) => {
                  setSelectedSolicitacao(row);
                  setOpenEditDialog(true);
                }}
                onViewHistorico={(row) => {
                  setHistoricoSolicitacao(row);
                  setOpenHistoricoModal(true);
                }}
                onResendEmail={handleResendEmail}
                resendingId={resendingId}
              />
            ) : (
              <NotFound />
            )}
          </>
        )}
      </Container>

      <EditSolicitacaoDialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setSelectedSolicitacao(null);
        }}
        solicitacao={selectedSolicitacao}
        onUpdated={() => handleSearch(page)}
      />

      <HistoricoModal
        open={openHistoricoModal}
        onClose={() => {
          setOpenHistoricoModal(false);
          setHistoricoSolicitacao(null);
        }}
        solicitacao={historicoSolicitacao}
      />
    </>
  );
}
